import {
  getGetUnitTypeQueryOptions,
  getListSensorsQueryKey,
  useBulkUpdateSensors,
  useListSensors,
  useListUnitTypes,
  type ApiError,
  type SensorsSensor,
  type UnittypesUnitResponse,
} from '@smarthome/api';
import { formatRelativeTime, ROUTES, useSensorStream, type SensorStreamEvent } from '@smarthome/ui';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Info, Star } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { formatReportingDuration, getReportingStabilityLabel } from './reportingSummary';

import {
  Button,
  Dropdown,
  SearchField,
  Select,
  cn,
  useToast,
  type DropdownItem,
  type SelectOption,
} from '@/components/ui';

type EditableField =
  | 'name'
  | 'unitTypeId'
  | 'unitSymbol'
  | 'reportingInterval'
  | 'alertThreshold'
  | 'isFavorite'
  | 'isMonotonic'
  | 'monotonicOffset';

type SensorEditableFields = {
  name: string;
  unitTypeId: string | null;
  unitSymbol: string | null;
  reportingInterval: string;
  alertThreshold: string;
  isFavorite: boolean;
  isMonotonic: boolean;
  monotonicOffset: string;
};

type RowErrorMap = Record<string, Record<string, string>>;

type SortOption = 'name' | 'provider' | 'lastSeen';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'provider', label: 'Provider' },
  { value: 'lastSeen', label: 'Last seen' },
];

const INTEGER_FORMATTER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const ONE_DECIMAL_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatValue(value?: number | null): string {
  if (value === undefined || value === null) return '--';
  if (Number.isInteger(value)) return INTEGER_FORMATTER.format(value);
  return ONE_DECIMAL_FORMATTER.format(value);
}

function isDynamicSensor(sensor: SensorsSensor): boolean {
  return (
    sensor.humanId?.startsWith('device:') ||
    sensor.humanId?.startsWith('scene:') ||
    sensor.provider === 'device' ||
    sensor.provider === 'scene'
  );
}

function toEditableFields(sensor: SensorsSensor): SensorEditableFields {
  return {
    name: sensor.name ?? '',
    unitTypeId: sensor.unitTypeId ?? null,
    unitSymbol: sensor.unitSymbol ?? null,
    reportingInterval: sensor.reportingInterval ? String(sensor.reportingInterval) : '',
    alertThreshold: sensor.alertThreshold ? String(sensor.alertThreshold) : '',
    isFavorite: sensor.isFavorite ?? false,
    isMonotonic: sensor.isMonotonic ?? false,
    monotonicOffset: String(sensor.monotonicOffset ?? 0),
  };
}

function applyDraftValue<K extends EditableField>(
  draft: Partial<SensorEditableFields>,
  base: SensorEditableFields,
  field: K,
  value: SensorEditableFields[K]
) {
  if (value === base[field]) {
    delete draft[field];
  } else {
    (draft as Record<K, SensorEditableFields[K]>)[field] = value;
  }
}

function normalizeErrorField(field: string): string {
  if (field === 'unitTypeID') return 'unitTypeId';
  return field;
}

type ReportingSummaryHintProps = {
  reportingSummary: SensorsSensor['reportingSummary'];
  highlighted?: boolean;
};

function ReportingSummaryHint({
  reportingSummary,
  highlighted = false,
}: ReportingSummaryHintProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const reportCount = reportingSummary?.reportCount ?? 0;

  const clearTimers = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    const panelWidth = panelRect?.width ?? 288;
    const panelHeight = panelRect?.height ?? 220;
    const viewportPadding = 12;
    const gap = 10;
    const availableBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
    const availableAbove = triggerRect.top - viewportPadding;
    const openUpward = availableBelow < panelHeight + gap && availableAbove > availableBelow;

    const top = openUpward
      ? Math.max(viewportPadding, triggerRect.top - panelHeight - gap)
      : Math.min(triggerRect.bottom + gap, window.innerHeight - panelHeight - viewportPadding);

    const left = Math.min(
      Math.max(viewportPadding, triggerRect.left + triggerRect.width / 2 - panelWidth / 2),
      window.innerWidth - panelWidth - viewportPadding
    );

    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleViewportChange = () => updatePosition();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setPosition(null);
      }
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const open = useCallback(() => {
    clearTimers();
    setIsOpen(true);
  }, [clearTimers]);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    openTimeoutRef.current = setTimeout(() => setIsOpen(true), 120);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setPosition(null);
    }, 120);
  }, [clearTimers]);

  const tooltipContent =
    isOpen && reportingSummary && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            role="tooltip"
            onMouseEnter={open}
            onMouseLeave={scheduleClose}
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            className="z-[9999] w-72 rounded-xl border border-white/10 bg-[#111113] px-4 py-3 text-left shadow-2xl"
          >
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
                    Observed Cadence
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {getReportingStabilityLabel(reportingSummary.stability)}
                  </p>
                </div>
                <p className="text-right text-xs text-zinc-400">
                  {reportCount > 0
                    ? `${reportCount.toLocaleString()} reports / 14d`
                    : 'No recent reports'}
                </p>
              </div>

              {reportCount > 0 ? (
                <>
                  {highlighted && (
                    <p className="text-xs leading-5 text-amber-300">
                      Configured thresholds are tighter than the observed cadence suggests.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/10 pt-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                        Typical
                      </p>
                      <p className="mt-1 text-sm font-medium text-zinc-100">
                        {formatReportingDuration(reportingSummary.medianIntervalSeconds)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                        Usual Max
                      </p>
                      <p className="mt-1 text-sm font-medium text-zinc-100">
                        {formatReportingDuration(reportingSummary.p90IntervalSeconds)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                        Longest Gap
                      </p>
                      <p className="mt-1 text-sm font-medium text-zinc-100">
                        {formatReportingDuration(reportingSummary.longestGapSeconds)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                        Suggested Stale
                      </p>
                      <p className="mt-1 text-sm font-medium text-zinc-100">
                        {formatReportingDuration(
                          reportingSummary.suggestedReportingIntervalSeconds
                        )}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                        Suggested Missing
                      </p>
                      <p className="mt-1 text-sm font-medium text-zinc-100">
                        {formatReportingDuration(reportingSummary.suggestedAlertThresholdSeconds)}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="border-t border-white/10 pt-3 text-xs leading-5 text-zinc-300">
                  No reports were recorded in the last 14 days.
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {!reportingSummary ? null : (
        <>
          <button
            ref={triggerRef}
            type="button"
            aria-label="View observed reporting cadence"
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            onFocus={open}
            onBlur={scheduleClose}
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors',
              highlighted
                ? 'border-amber-200 bg-amber-50 text-amber-600 hover:border-amber-300 hover:text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:border-amber-500/50 dark:hover:text-amber-200'
                : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-zinc-400 dark:hover:border-white/[0.12] dark:hover:text-zinc-200'
            )}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
          {tooltipContent}
        </>
      )}
    </>
  );
}

type UnitSelectProps = {
  value: string;
  items: DropdownItem[];
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  missingUnit?: boolean;
};

function UnitSelect({ value, items, onChange, error, disabled, missingUnit }: UnitSelectProps) {
  const selectedItem = items.find((item) => item.value === value);
  const displayLabel =
    selectedItem?.label ??
    (missingUnit ? 'Unit required' : value === 'none' ? 'None' : 'Loading...');
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [minWidth, setMinWidth] = useState<number | null>(null);

  const measureEntries = useMemo(() => {
    const entries: Array<{ text: string; className: string }> = [];
    items.forEach((item) => {
      if (item.label) {
        entries.push({ text: item.label, className: 'text-sm font-normal' });
      }
      if (item.description) {
        entries.push({ text: item.description, className: 'text-xs font-normal' });
      }
    });
    ['None', 'Loading...', 'Unit required'].forEach((text) => {
      entries.push({ text, className: 'text-sm font-normal' });
    });
    return entries;
  }, [items]);

  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const nodes = Array.from(measureRef.current.querySelectorAll<HTMLElement>('[data-measure]'));
    if (nodes.length === 0) return;

    let maxWidth = 0;
    nodes.forEach((node) => {
      maxWidth = Math.max(maxWidth, node.scrollWidth);
    });

    const paddingAndChevron = 48;
    setMinWidth(Math.ceil(maxWidth + paddingAndChevron));
  }, [measureEntries]);

  return (
    <div className="relative">
      <label className="sr-only">Unit</label>
      <div
        className={cn(disabled && 'pointer-events-none opacity-50')}
        style={minWidth ? { minWidth } : undefined}
      >
        <Dropdown
          items={items}
          value={value}
          onSelect={onChange}
          trigger={({ isOpen }) => (
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-sm transition-colors',
                'border-gray-300 bg-white text-gray-900',
                'dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white',
                error && 'border-red-500 dark:border-red-500',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              style={minWidth ? { minWidth } : undefined}
            >
              <span className="truncate">{displayLabel}</span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 flex-shrink-0 text-gray-500 transition-transform dark:text-zinc-500',
                  isOpen && 'rotate-180'
                )}
              />
            </button>
          )}
        />
      </div>
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none absolute -z-10 whitespace-nowrap opacity-0"
      >
        {measureEntries.map((entry, index) => (
          <span key={`${entry.text}-${index}`} data-measure className={entry.className}>
            {entry.text}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SensorsBulkEditPage() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [drafts, setDrafts] = useState<Record<string, Partial<SensorEditableFields>>>({});
  const [errors, setErrors] = useState<RowErrorMap>({});
  const [baseline, setBaseline] = useState<Record<string, SensorEditableFields> | null>(null);
  const [liveReadings, setLiveReadings] = useState<
    Record<string, { value?: number | null; lastSeen?: string | null }>
  >({});

  const { data, isLoading, isError, refetch, isFetching } = useListSensors({
    query: {
      refetchOnWindowFocus: false,
    },
  });

  const { data: unitTypesData, isLoading: isLoadingUnitTypes } = useListUnitTypes();

  const unitTypeIds = useMemo(() => {
    return (unitTypesData?.unitTypes ?? [])
      .map((unitType) => unitType.id)
      .filter((id): id is string => !!id);
  }, [unitTypesData?.unitTypes]);

  const unitTypeQueries = useQueries({
    queries: unitTypeIds.map((id) =>
      getGetUnitTypeQueryOptions(id, {
        query: {
          enabled: !!id,
          staleTime: 5 * 60 * 1000,
        },
      })
    ),
  });

  const unitTypeUnits = useMemo(() => {
    const map = new Map<string, UnittypesUnitResponse[]>();
    unitTypeQueries.forEach((query) => {
      const unitType = query.data;
      if (unitType?.id) {
        map.set(unitType.id, unitType.units ?? []);
      }
    });
    return map;
  }, [unitTypeQueries]);

  const sensors = useMemo(() => data?.sensors ?? [], [data?.sensors]);
  const definedSensors = useMemo(
    () => sensors.filter((sensor) => !isDynamicSensor(sensor)),
    [sensors]
  );

  const baseFromSensors = useMemo(() => {
    const nextBase: Record<string, SensorEditableFields> = {};
    definedSensors.forEach((sensor) => {
      if (sensor.humanId) {
        nextBase[sensor.humanId] = toEditableFields(sensor);
      }
    });
    return nextBase;
  }, [definedSensors]);

  const hasUnsavedChanges = useMemo(() => Object.keys(drafts).length > 0, [drafts]);

  useEffect(() => {
    if (!baseline && definedSensors.length > 0) {
      setBaseline(baseFromSensors);
    }
  }, [baseline, baseFromSensors, definedSensors.length]);

  useEffect(() => {
    if (!baseline) return;
    let hasNew = false;
    const next = { ...baseline };

    definedSensors.forEach((sensor) => {
      if (!sensor.humanId) return;
      if (!next[sensor.humanId]) {
        next[sensor.humanId] = toEditableFields(sensor);
        hasNew = true;
      }
    });

    if (hasNew) {
      setBaseline(next);
    }
  }, [baseline, definedSensors]);

  const handleSensorEvent = useCallback((event: SensorStreamEvent) => {
    if (event.type !== 'value_changed') return;
    if (!event.humanId && !event.sensorId) return;

    setLiveReadings((prev) => {
      const next = { ...prev };
      const keys = [event.humanId, event.sensorId].filter(Boolean) as string[];
      keys.forEach((key) => {
        next[key] = {
          value: event.value ?? prev[key]?.value ?? null,
          lastSeen: event.timestamp ?? prev[key]?.lastSeen ?? null,
        };
      });
      return next;
    });
  }, []);

  useSensorStream({
    enabled: true,
    onEvent: handleSensorEvent,
  });

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const baseFieldsById = baseline ?? baseFromSensors;

  const providerOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = [{ value: 'all', label: 'All providers' }];
    const providers = Array.from(
      new Set(
        definedSensors
          .map((sensor) => sensor.provider)
          .filter((p): p is string => typeof p === 'string' && p.length > 0)
      )
    ).sort();
    providers.forEach((provider) => options.push({ value: provider, label: provider }));
    return options;
  }, [definedSensors]);

  const sortOptions: SelectOption[] = useMemo(
    () =>
      SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    []
  );

  const unitSelectItems = useMemo(() => {
    const items: DropdownItem[] = [{ value: 'none', label: 'None' }];

    (unitTypesData?.unitTypes ?? [])
      .filter((unitType) => unitType.id && unitType.name)
      .forEach((unitType) => {
        items.push({ type: 'divider' });

        items.push({
          type: 'header',
          label: unitType.name ?? 'Unit type',
          description: unitType.unitCount
            ? `${unitType.unitCount} unit${unitType.unitCount === 1 ? '' : 's'}`
            : undefined,
        });

        const typeId = unitType.id ?? '';
        const units = typeId ? unitTypeUnits.get(typeId) : undefined;
        if (!units) {
          items.push({
            value: `${typeId}|loading`,
            label: 'Loading units...',
            disabled: true,
          });
          return;
        }

        if (units.length === 0) {
          items.push({
            value: `${typeId}|empty`,
            label: 'No units available',
            disabled: true,
          });
          return;
        }

        units
          .filter((unit) => unit.symbol)
          .forEach((unit) => {
            const symbol = unit.symbol ?? '';
            const label = unit.label ? `${unit.label} (${symbol})` : symbol;
            items.push({
              value: `${typeId}|${symbol}`,
              label,
            });
          });
      });

    return items;
  }, [unitTypeUnits, unitTypesData?.unitTypes]);

  const rows = useMemo(() => {
    const rowsWithFields = definedSensors
      .filter((sensor) => sensor.humanId)
      .map((sensor) => {
        const baseFields = baseFieldsById[sensor.humanId ?? ''] ?? toEditableFields(sensor);
        const draftFields = drafts[sensor.humanId ?? ''] ?? {};
        return {
          sensor,
          fields: {
            ...baseFields,
            ...draftFields,
          },
        };
      });

    let filtered = rowsWithFields;

    if (providerFilter !== 'all') {
      filtered = filtered.filter((row) => row.sensor.provider === providerFilter);
    }

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((row) => {
        return (
          row.fields.name.toLowerCase().includes(searchLower) ||
          row.sensor.humanId?.toLowerCase().includes(searchLower) ||
          row.sensor.provider?.toLowerCase().includes(searchLower)
        );
      });
    }

    filtered.sort((left, right) => {
      switch (sortBy) {
        case 'provider':
          return (left.sensor.provider || '').localeCompare(right.sensor.provider || '');
        case 'lastSeen': {
          const leftTime = left.sensor.lastSeen ? new Date(left.sensor.lastSeen).getTime() : 0;
          const rightTime = right.sensor.lastSeen ? new Date(right.sensor.lastSeen).getTime() : 0;
          return rightTime - leftTime;
        }
        case 'name':
        default:
          return left.fields.name.localeCompare(right.fields.name);
      }
    });

    return filtered;
  }, [baseFieldsById, definedSensors, drafts, providerFilter, search, sortBy]);

  const rowCount = rows.length;

  const focusCell = useCallback((rowIndex: number, colIndex: number) => {
    const target = tableRef.current?.querySelector<HTMLElement>(
      `[data-grid-cell][data-row="${rowIndex}"][data-col="${colIndex}"]`
    );
    target?.focus();
  }, []);

  const handleGridKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        focusCell(Math.min(rowIndex + 1, rowCount - 1), colIndex);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusCell(Math.min(rowIndex + 1, rowCount - 1), colIndex);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusCell(Math.max(rowIndex - 1, 0), colIndex);
      }
    },
    [focusCell, rowCount]
  );

  const setField = useCallback(
    (humanId: string, field: EditableField, value: SensorEditableFields[EditableField]) => {
      const base = baseFieldsById[humanId];
      if (!base) return;

      setDrafts((prev) => {
        const next = { ...prev };
        const draft = { ...(next[humanId] ?? {}) };

        applyDraftValue(draft, base, field, value);

        if (field === 'unitTypeId') {
          applyDraftValue(draft, base, 'unitSymbol', null);
        }

        if (field === 'isMonotonic' && value === false) {
          applyDraftValue(draft, base, 'monotonicOffset', '0');
        }

        if (Object.keys(draft).length === 0) {
          delete next[humanId];
        } else {
          next[humanId] = draft;
        }

        return next;
      });

      setErrors((prev) => {
        if (!prev[humanId]) return prev;
        const nextRow = { ...prev[humanId] };
        delete nextRow[field];
        if (field === 'unitTypeId') {
          delete nextRow['unitSymbol'];
        }
        if (field === 'isMonotonic') {
          delete nextRow['monotonicOffset'];
        }
        const next = { ...prev };
        if (Object.keys(nextRow).length === 0) {
          delete next[humanId];
        } else {
          next[humanId] = nextRow;
        }
        return next;
      });
    },
    [baseFieldsById]
  );

  const setUnitSelection = useCallback(
    (humanId: string, unitTypeId: string | null, unitSymbol: string | null) => {
      const base = baseFieldsById[humanId];
      if (!base) return;

      const nextUnitSymbol = unitTypeId ? unitSymbol : null;

      setDrafts((prev) => {
        const next = { ...prev };
        const draft = { ...(next[humanId] ?? {}) };

        applyDraftValue(draft, base, 'unitTypeId', unitTypeId);
        applyDraftValue(draft, base, 'unitSymbol', nextUnitSymbol);

        if (Object.keys(draft).length === 0) {
          delete next[humanId];
        } else {
          next[humanId] = draft;
        }

        return next;
      });

      setErrors((prev) => {
        if (!prev[humanId]) return prev;
        const nextRow = { ...prev[humanId] };
        delete nextRow['unitTypeId'];
        delete nextRow['unitSymbol'];
        const next = { ...prev };
        if (Object.keys(nextRow).length === 0) {
          delete next[humanId];
        } else {
          next[humanId] = nextRow;
        }
        return next;
      });
    },
    [baseFieldsById]
  );

  const bulkUpdateMutation = useBulkUpdateSensors();

  const buildUpdates = useCallback(() => {
    const nextErrors: RowErrorMap = {};
    const updates = Object.keys(drafts).flatMap((humanId) => {
      const base = baseFieldsById[humanId];
      if (!base) return [];

      const merged: SensorEditableFields = {
        ...base,
        ...drafts[humanId],
      };

      const rowErrors: Record<string, string> = {};

      if (!merged.name) {
        rowErrors['name'] = 'Name is required';
      } else if (merged.name.length > 255) {
        rowErrors['name'] = 'Name must be 255 characters or less';
      }

      const reportingInterval = merged.reportingInterval;
      let reportingIntervalValue: number | null = null;
      if (reportingInterval !== '') {
        const parsed = Number(reportingInterval);
        if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
          rowErrors['reportingInterval'] = 'Must be a whole number';
        } else if (parsed < 1) {
          rowErrors['reportingInterval'] = 'Must be at least 1 second';
        } else if (parsed > 86400) {
          rowErrors['reportingInterval'] = 'Cannot exceed 24 hours';
        } else {
          reportingIntervalValue = parsed;
        }
      }

      const alertThreshold = merged.alertThreshold;
      let alertThresholdValue: number | null = null;
      if (alertThreshold !== '') {
        const parsed = Number(alertThreshold);
        if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
          rowErrors['alertThreshold'] = 'Must be a whole number';
        } else if (parsed < 1) {
          rowErrors['alertThreshold'] = 'Must be at least 1 second';
        } else if (parsed > 604800) {
          rowErrors['alertThreshold'] = 'Cannot exceed 7 days';
        } else {
          alertThresholdValue = parsed;
        }
      }

      let monotonicOffsetValue = 0;
      if (merged.isMonotonic) {
        if (!merged.monotonicOffset.trim()) {
          rowErrors['monotonicOffset'] = 'Offset is required for monotonic sensors';
        } else {
          const parsed = Number(merged.monotonicOffset);
          if (!Number.isFinite(parsed)) {
            rowErrors['monotonicOffset'] = 'Offset must be a number';
          } else {
            monotonicOffsetValue = parsed;
          }
        }
      } else if (merged.monotonicOffset.trim() !== '0') {
        rowErrors['monotonicOffset'] = 'Offset must be 0 when monotonic is off';
      }

      const unitTypeId = merged.unitTypeId;
      const unitSymbol = merged.unitSymbol;

      if (!unitTypeId && unitSymbol) {
        rowErrors['unitSymbol'] = 'Unit requires a unit type';
      }

      if (unitTypeId && !unitSymbol) {
        rowErrors['unitSymbol'] = 'Unit is required when unit type is set';
      }

      if (unitTypeId && unitSymbol) {
        const units = unitTypeUnits.get(unitTypeId);
        if (units && !units.some((unit) => unit.symbol === unitSymbol)) {
          rowErrors['unitSymbol'] = 'Unit does not belong to unit type';
        }
      }

      if (Object.keys(rowErrors).length > 0) {
        nextErrors[humanId] = rowErrors;
        return [];
      }

      return [
        {
          humanId,
          name: merged.name,
          unitTypeID: unitTypeId ?? undefined,
          unitSymbol: unitSymbol ?? undefined,
          reportingInterval: reportingIntervalValue ?? undefined,
          alertThreshold: alertThresholdValue ?? undefined,
          isFavorite: merged.isFavorite,
          isMonotonic: merged.isMonotonic,
          monotonicOffset: monotonicOffsetValue,
        },
      ];
    });

    return { updates, nextErrors };
  }, [baseFieldsById, drafts, unitTypeUnits]);

  const handleSave = useCallback(async () => {
    if (Object.keys(drafts).length === 0) return;

    const { updates, nextErrors } = buildUpdates();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      addToast({ message: 'Fix validation errors before saving.', variant: 'error' });
      return;
    }

    try {
      const response = await bulkUpdateMutation.mutateAsync({
        data: { updates },
      });

      if (response?.errors?.length) {
        const serverErrors: RowErrorMap = {};
        response.errors.forEach((err) => {
          if (!err.humanId || !err.field || !err.message) return;
          const field = normalizeErrorField(err.field);
          if (!serverErrors[err.humanId]) {
            serverErrors[err.humanId] = {};
          }
          serverErrors[err.humanId]![field] = err.message;
        });
        setErrors(serverErrors);
        addToast({ message: 'Some rows could not be saved.', variant: 'error' });
        return;
      }

      queryClient.setQueryData<{ sensors: SensorsSensor[] }>(getListSensorsQueryKey(), (old) => {
        if (!old?.sensors) return old;
        const updatedMap = new Map(
          (response?.sensors ?? []).map((sensor) => [sensor.humanId, sensor])
        );
        return {
          ...old,
          sensors: old.sensors.map((sensor) =>
            sensor.humanId && updatedMap.has(sensor.humanId)
              ? (updatedMap.get(sensor.humanId) ?? sensor)
              : sensor
          ),
        };
      });

      const updatedSensors = response?.sensors;
      if (updatedSensors?.length) {
        setBaseline((prev) => {
          const next = { ...(prev ?? {}) };
          updatedSensors.forEach((sensor) => {
            if (sensor.humanId) {
              next[sensor.humanId] = toEditableFields(sensor);
            }
          });
          return next;
        });
      }

      setDrafts({});
      setErrors({});
      addToast({ message: 'Sensors updated.', variant: 'success' });
    } catch (error) {
      const apiError = error as ApiError | undefined;
      const responseData = apiError?.response?.data as
        | {
            errors?: Array<{ humanId?: string; field?: string; message?: string }>;
          }
        | undefined;
      if (responseData?.errors?.length) {
        const serverErrors: RowErrorMap = {};
        responseData.errors.forEach((err) => {
          if (!err.humanId || !err.field || !err.message) return;
          const field = normalizeErrorField(err.field);
          if (!serverErrors[err.humanId]) {
            serverErrors[err.humanId] = {};
          }
          serverErrors[err.humanId]![field] = err.message;
        });
        setErrors(serverErrors);
        addToast({ message: 'Some rows could not be saved.', variant: 'error' });
        return;
      }

      addToast({ message: 'Failed to save sensor updates.', variant: 'error' });
    }
  }, [addToast, bulkUpdateMutation, buildUpdates, drafts, queryClient]);

  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    navigate(ROUTES.SENSORS);
  }, [hasUnsavedChanges, navigate]);

  if (isError) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
        <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load sensors.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            type="button"
            onClick={handleNavigateBack}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sensors
          </button>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Bulk Edit Sensors
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
            Edit non-dynamic sensors in a single view. Changes save only when you click Save.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching} size="sm">
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={isFetching || !hasUnsavedChanges || bulkUpdateMutation.isPending}
            loading={bulkUpdateMutation.isPending}
            size="sm"
          >
            Save changes
          </Button>
        </div>
      </div>

      {hasUnsavedChanges && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          You have unsaved changes.
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-72">
            <SearchField
              label="Search"
              hideLabel
              placeholder="Search by name, human ID, or provider"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="w-full sm:w-auto">
            <Select
              label="Provider"
              hideLabel
              placeholder="Provider"
              value={providerFilter}
              onChange={(value) => setProviderFilter(value)}
              options={providerOptions}
            />
          </div>
          <div className="w-full sm:w-auto">
            <Select
              label="Sort"
              hideLabel
              placeholder="Sort"
              value={sortBy}
              onChange={(value) => setSortBy(value as SortOption)}
              options={sortOptions}
            />
          </div>
        </div>
      </div>

      <div
        ref={tableRef}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 dark:text-zinc-400">
            No sensors match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1300px] w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-white/[0.03] dark:text-zinc-400">
                <tr>
                  <th className="sticky left-0 z-50 min-w-[360px] border-r border-gray-200 bg-gray-50 px-4 py-3 dark:border-white/[0.06] dark:bg-[#18181b]">
                    Sensor
                  </th>
                  <th className="w-12 px-2 py-3 text-center">
                    <span className="sr-only">Favorite</span>
                  </th>
                  <th className="px-4 py-3">Unit</th>
                  <th className="px-4 py-3">Reporting Interval</th>
                  <th className="px-4 py-3">Alert Threshold</th>
                  <th className="px-4 py-3">Monotonic</th>
                  <th className="px-4 py-3">Offset</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">Last Seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                {rows.map(({ sensor, fields }, rowIndex) => {
                  const humanId = sensor.humanId ?? '';
                  const rowErrors = errors[humanId] ?? {};
                  const rowHasErrors = Object.keys(rowErrors).length > 0;
                  const liveReading =
                    (humanId && liveReadings[humanId]) ||
                    (sensor.id ? liveReadings[sensor.id] : undefined);
                  const displayValue = liveReading?.value ?? sensor.value;
                  const displayLastSeen = liveReading?.lastSeen ?? sensor.lastSeen;
                  const missingUnit = !!(fields.unitTypeId && !fields.unitSymbol);
                  const unitSelectValue =
                    fields.unitTypeId && fields.unitSymbol
                      ? `${fields.unitTypeId}|${fields.unitSymbol}`
                      : 'none';
                  const unitError = rowErrors['unitSymbol'] ?? rowErrors['unitTypeId'];
                  const reportingSummary = sensor.reportingSummary;
                  const configuredReportingInterval = fields.reportingInterval
                    ? Number(fields.reportingInterval)
                    : null;
                  const configuredAlertThreshold = fields.alertThreshold
                    ? Number(fields.alertThreshold)
                    : null;
                  const reportingBelowSuggestion =
                    configuredReportingInterval !== null &&
                    reportingSummary?.suggestedReportingIntervalSeconds !== undefined &&
                    configuredReportingInterval <
                      reportingSummary.suggestedReportingIntervalSeconds;
                  const alertBelowSuggestion =
                    configuredAlertThreshold !== null &&
                    reportingSummary?.suggestedAlertThresholdSeconds !== undefined &&
                    configuredAlertThreshold < reportingSummary.suggestedAlertThresholdSeconds;
                  const hasCadenceWarning = reportingBelowSuggestion || alertBelowSuggestion;

                  return (
                    <tr
                      key={humanId}
                      className={cn(
                        'align-middle',
                        rowHasErrors && 'bg-red-50/40 dark:bg-red-500/5'
                      )}
                    >
                      <td
                        className={cn(
                          'sticky left-0 z-40 min-w-[360px] border-r px-4 py-3',
                          'border-gray-200 dark:border-white/[0.06]',
                          rowHasErrors
                            ? 'bg-red-50/40 dark:bg-red-500/5'
                            : 'bg-white dark:bg-[#18181b]'
                        )}
                      >
                        <div className="space-y-2">
                          <div>
                            <div
                              className="truncate whitespace-nowrap text-xs text-gray-500 dark:text-zinc-400"
                              title={`${sensor.humanId}${sensor.provider ? ` \u00b7 ${sensor.provider}` : ''}`}
                            >
                              <span className="font-mono">{sensor.humanId}</span>
                              {sensor.provider && (
                                <>
                                  <span className="mx-1 text-gray-400 dark:text-zinc-500">
                                    &middot;
                                  </span>
                                  <span className="font-medium text-gray-400 dark:text-zinc-500">
                                    {sensor.provider}
                                  </span>
                                </>
                              )}
                            </div>
                            {rowErrors['humanId'] && (
                              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                {rowErrors['humanId']}
                              </p>
                            )}
                          </div>
                          <div>
                            <input
                              type="text"
                              value={fields.name}
                              onChange={(event) => setField(humanId, 'name', event.target.value)}
                              onKeyDown={(event) => handleGridKeyDown(event, rowIndex, 0)}
                              data-grid-cell
                              data-row={rowIndex}
                              data-col={0}
                              aria-label="Sensor name"
                              className={cn(
                                'w-full rounded-md border px-2.5 py-2 text-sm',
                                'border-gray-300 bg-white text-gray-900',
                                'dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white',
                                rowErrors['name'] && 'border-red-500'
                              )}
                            />
                            {rowErrors['name'] && (
                              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                {rowErrors['name']}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="w-12 px-2 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setField(humanId, 'isFavorite', !fields.isFavorite)}
                          onKeyDown={(event) => handleGridKeyDown(event, rowIndex, 1)}
                          data-grid-cell
                          data-row={rowIndex}
                          data-col={1}
                          aria-label={fields.isFavorite ? 'Unset favorite' : 'Set favorite'}
                          aria-pressed={fields.isFavorite}
                          className={cn(
                            'flex items-center justify-center rounded-md border px-2.5 py-2',
                            'border-gray-300 text-gray-500 hover:text-yellow-500',
                            'dark:border-white/[0.06] dark:text-zinc-400 dark:hover:text-yellow-400',
                            fields.isFavorite && 'text-yellow-500 dark:text-yellow-400'
                          )}
                        >
                          <Star
                            className="h-4 w-4"
                            fill={fields.isFavorite ? 'currentColor' : 'none'}
                          />
                        </button>
                        {rowErrors['isFavorite'] && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {rowErrors['isFavorite']}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <UnitSelect
                          value={unitSelectValue}
                          items={unitSelectItems}
                          error={unitError}
                          disabled={isLoadingUnitTypes}
                          missingUnit={missingUnit}
                          onChange={(value) => {
                            if (value === 'none') {
                              setUnitSelection(humanId, null, null);
                              return;
                            }
                            const [typeId, symbol] = value.split('|');
                            if (!typeId) {
                              setUnitSelection(humanId, null, null);
                              return;
                            }
                            if (!symbol || symbol === 'none') {
                              setUnitSelection(humanId, typeId, null);
                              return;
                            }
                            if (symbol === 'loading' || symbol === 'empty') {
                              return;
                            }
                            setUnitSelection(humanId, typeId, symbol);
                          }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={fields.reportingInterval}
                          onChange={(event) =>
                            setField(humanId, 'reportingInterval', event.target.value)
                          }
                          onKeyDown={(event) => handleGridKeyDown(event, rowIndex, 2)}
                          data-grid-cell
                          data-row={rowIndex}
                          data-col={2}
                          aria-label="Reporting interval in seconds"
                          className={cn(
                            'w-full rounded-md border px-2.5 py-2 text-sm',
                            'border-gray-300 bg-white text-gray-900',
                            'dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white',
                            reportingBelowSuggestion &&
                              !rowErrors['reportingInterval'] &&
                              'border-amber-300 bg-amber-50/40 dark:border-amber-500/40 dark:bg-amber-500/5',
                            rowErrors['reportingInterval'] && 'border-red-500'
                          )}
                          placeholder="Seconds"
                          min={1}
                          max={86400}
                        />
                        {rowErrors['reportingInterval'] && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {rowErrors['reportingInterval']}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={fields.alertThreshold}
                            onChange={(event) =>
                              setField(humanId, 'alertThreshold', event.target.value)
                            }
                            onKeyDown={(event) => handleGridKeyDown(event, rowIndex, 3)}
                            data-grid-cell
                            data-row={rowIndex}
                            data-col={3}
                            aria-label="Alert threshold in seconds"
                            className={cn(
                              'min-w-0 flex-1 rounded-md border px-2.5 py-2 text-sm',
                              'border-gray-300 bg-white text-gray-900',
                              'dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white',
                              alertBelowSuggestion &&
                                !rowErrors['alertThreshold'] &&
                                'border-amber-300 bg-amber-50/40 dark:border-amber-500/40 dark:bg-amber-500/5',
                              rowErrors['alertThreshold'] && 'border-red-500'
                            )}
                            placeholder="Seconds"
                            min={1}
                            max={604800}
                          />
                          <ReportingSummaryHint
                            reportingSummary={reportingSummary}
                            highlighted={hasCadenceWarning}
                          />
                        </div>
                        {rowErrors['alertThreshold'] && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {rowErrors['alertThreshold']}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={fields.isMonotonic}
                            onChange={(event) =>
                              setField(humanId, 'isMonotonic', event.target.checked)
                            }
                            onKeyDown={(event) => handleGridKeyDown(event, rowIndex, 4)}
                            data-grid-cell
                            data-row={rowIndex}
                            data-col={4}
                            aria-label="Monotonic sensor"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                        {rowErrors['isMonotonic'] && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {rowErrors['isMonotonic']}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={fields.monotonicOffset}
                          onChange={(event) =>
                            setField(humanId, 'monotonicOffset', event.target.value)
                          }
                          onKeyDown={(event) => handleGridKeyDown(event, rowIndex, 5)}
                          data-grid-cell
                          data-row={rowIndex}
                          data-col={5}
                          aria-label="Monotonic offset"
                          className={cn(
                            'w-full rounded-md border px-2.5 py-2 text-sm',
                            'border-gray-300 bg-white text-gray-900',
                            'dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white',
                            !fields.isMonotonic && 'opacity-50',
                            rowErrors['monotonicOffset'] && 'border-red-500'
                          )}
                          disabled={!fields.isMonotonic}
                          step="any"
                        />
                        {rowErrors['monotonicOffset'] && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {rowErrors['monotonicOffset']}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatValue(displayValue)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-zinc-400 whitespace-nowrap">
                        {displayLastSeen ? formatRelativeTime(displayLastSeen) : 'Never'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
